// src/services/userRoleService.js

import apiClient from "./apiClient";
import axios from "axios";
import { BACKEND_URL, USERMGMT_URL, getEnvVar } from "../utils/envConfig";
import userManagementApi from "./userManagementApi";
import { getAllHospitalOrganizations } from "./hospitalOrganizationService";
import subscriptionRoleLimitsService from "./subscriptionRoleLimitsService";
const API_URL = `/staff`; // Use relative path since apiClient has baseURL
const USER_MGMT_URL = USERMGMT_URL;

// Maximum organizationId to try when searching for a user (fallback if we can't get list from backend)
// Can be overridden via environment variable REACT_APP_MAX_ORG_ID
// Default is 1000, but can be increased if you have more organizations
const MAX_ORGANIZATION_ID = parseInt(getEnvVar("REACT_APP_MAX_ORG_ID", "1000"), 10);

// getAllStaff is called from dozens of places (nurse/kardex forms, IPD reports,
// appointment modals, etc.) — often several times on a single page load. Cache
// it the same way getAllDoctors is cached, so those collapse into one network
// call instead of one per caller.
let __allStaffCache = { data: null, timestamp: 0, inFlight: null, cacheKey: null };
const STAFF_CACHE_TTL_MS = 5 * 60 * 1000;

export const invalidateStaffCache = () => {
  __allStaffCache = { data: null, timestamp: 0, inFlight: null, cacheKey: null };
};

// export const createStaff = async (staff) => {
//   const response = await axios.post(API_URL, staff);
//   if (response.status === 200) {
//     const data = {         
//       fullName: response.data.name,
//       password: response.data.password,
//       role: response.data.userRole,
//       email: response.data.email,
//       department: response.data.departmentName ? response.data.departmentName : "",
//       mobile: response.data.mobile,
//     };
//     const userResponse = await fetch(`${USER_MGMT_URL}/register`, {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify(data),        
//     });
//   }
//   return response.data;
// };

export const createStaff = async (staff) => {
  // Get organizationId from localStorage for user registration
  // Only include if it's a valid positive number (common users won't have it)
  const storedUser = localStorage.getItem("user");
  let organizationId = null;
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      const orgId = user?.organizationId;
      // Only use organizationId if it's a valid positive number
      if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
        organizationId = Number(orgId);
      }
    } catch (e) {
      console.error("Error parsing stored user:", e);
    }
  }
  
  // Check subscription role limits before creating staff
  if (organizationId && staff.userRole) {
    try {
      const limitCheck = await subscriptionRoleLimitsService.checkRoleLimit(
        organizationId,
        staff.userRole
      );
      
      if (!limitCheck.allowed) {
        const error = new Error(limitCheck.message);
        error.limitCheck = limitCheck;
        throw error;
      }
    } catch (error) {
      // If it's a limit check error, re-throw it
      if (error.limitCheck) {
        throw error;
      }
      // For other errors (e.g., API errors), log but allow creation to proceed
      // This ensures the system doesn't break if the subscription service is down
      console.warn("Error checking subscription role limits, allowing creation to proceed:", error);
    }
  }
  
  const userData = {
    fullName: staff.name,
    password: staff.password,
    role: staff.userRole,
    email: staff.email,
    department: staff.departmentName || "",
    mobile: staff.mobile,
    // Only include organizationId if valid (common users won't have it)
    ...(organizationId && organizationId > 0 && { organizationId }),
  };
  // Register the user
  const userResponse = await fetch(`${USER_MGMT_URL}/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });

  let createdUser = null;
  const responseText = await userResponse.text();
  if (responseText) {
    try {
      createdUser = JSON.parse(responseText);
    } catch (e) {
      console.error("[StaffService] Failed to parse register response:", responseText);
    }
  }

  if (!userResponse.ok) {
    const errorMsg =
      createdUser?.message || createdUser?.error || "User registration failed";
    const err = new Error(errorMsg);
    err.response = {
      status: userResponse.status,
      data: createdUser || { message: errorMsg },
    };
    throw err;
  }

  if (!createdUser || !createdUser.id) {
    throw new Error(
      "User with this mobile number may already exist. Please check and try again."
    );
  }

  staff.userId = createdUser.id;
  
  // Workaround: Update organization_id if it's null in the response
  // This happens when the user management backend doesn't store organization_id during registration
  // Similar to how admin user creation works in onboarding
  if ((createdUser.organization_id === null || createdUser.organization_id === undefined) && organizationId) {
    try {
      // Use snake_case field names to match the updateUser endpoint format
      const updatePayload = {
        id: createdUser.id,
        full_name: staff.name,
        user_pwd: staff.password, // Include password (required field)
        user_role: staff.userRole,
        email: staff.email,
        department: staff.departmentName || "",
        mobile: staff.mobile,
        organization_id: organizationId, // Include organization_id in update payload
      };
      
      try {
        await userManagementApi.post(
          "/updateUser",
          updatePayload
        );

        console.log(
          "Updated organization_id for user:",
          createdUser.id
        );
      } catch (updateError) {
        console.warn(
          "Error updating organization_id:",
          updateError
        );

        // Don't fail the staff creation
      }
    } catch (updateError) {
      console.warn("Error updating organization_id:", updateError);
      // Don't fail the whole operation if organization_id update fails
    }
  }
  
  // apiClient will automatically add organizationId via interceptor
  const response = await apiClient.post(API_URL, staff);

  if (response.status !== 200) {
    throw new Error('Staff creation failed');
  }
  invalidateStaffCache();
  return response.data;
};

export const getAllStaff = async (options = {}) => {
  const { forceRefresh = false } = options;
  const now = Date.now();

  // Cache key includes organizationId so switching orgs (or a common user
  // with none) never serves another org's cached staff list.
  const storedUser = localStorage.getItem("user");
  let organizationId = null;
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      const orgId = user?.organizationId;
      if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
        organizationId = Number(orgId);
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  const cacheKey = organizationId && organizationId > 0 ? `org_${organizationId}` : "all";

  if (forceRefresh) {
    invalidateStaffCache();
  } else if (
    __allStaffCache.data &&
    __allStaffCache.cacheKey === cacheKey &&
    now - __allStaffCache.timestamp < STAFF_CACHE_TTL_MS
  ) {
    return __allStaffCache.data;
  }
  if (__allStaffCache.inFlight && __allStaffCache.cacheKey === cacheKey) {
    return __allStaffCache.inFlight;
  }

  // apiClient will automatically add organizationId as query param via interceptor
  // If user has no organizationId, backend should use base table
  __allStaffCache.inFlight = apiClient
    .get(`${API_URL}/all`)
    .then((response) => {
      const data = response.data;
      // Return data as-is - backend should handle filtering via organizationId query param
      // Client-side filtering removed as it was causing issues with valid organization data
      __allStaffCache.data = data;
      __allStaffCache.timestamp = Date.now();
      __allStaffCache.cacheKey = cacheKey;
      return data;
    })
    .catch((error) => {
      // Handle case where organization-specific table doesn't exist yet or backend error
      if (
        error.response?.status === 500 &&
        (error.response?.data?.message?.includes("Failed to retrieve") ||
          error.response?.data?.message?.includes("from table") ||
          error.response?.data?.message?.includes("does not exist"))
      ) {
        console.warn("Organization-specific table not found, returning empty array:", error.response.data.message);
        const emptyData = [];
        __allStaffCache.data = emptyData;
        __allStaffCache.timestamp = Date.now();
        __allStaffCache.cacheKey = cacheKey;
        return emptyData;
      }
      console.error("Error fetching staff:", error);
      throw error;
    })
    .finally(() => {
      __allStaffCache.inFlight = null;
    });

  return __allStaffCache.inFlight;
};

export const getStaffDetailByUserId = async (userId) => {
  const response = await apiClient.get(`${API_URL}/user/${userId}`, { timeout: 10000 });
  return response.data;
};

/**
 * Get staff by mobile number and organizationId
 * This is more reliable than searching by userId when login API returns wrong userId
 * @param {string} mobile - Mobile number
 * @param {number} organizationId - Organization ID (required)
 * @returns {Promise<Object>} Staff record
 */
export const getStaffByMobile = async (mobile, organizationId) => {
  if (!mobile || !organizationId) {
    throw new Error("Mobile number and organizationId are required");
  }
  
  try {
    console.log(`[staffService] Searching staff by mobile ${mobile} in organization ${organizationId}`);
    const response = await apiClient.get(`${API_URL}/by-mobile`, {
      params: { 
        mobile: mobile.trim(),
        organizationId: Number(organizationId)
      }
    });
    
    const data = response.data;
    console.log(`[staffService] ✅ Staff found by mobile ${mobile} in organization ${organizationId}`);
    
    // Verify the response has matching organizationId
    const orgIdFromResponse = data?.organizationId || data?.organization_id;
    if (orgIdFromResponse && Number(orgIdFromResponse) === Number(organizationId)) {
      return data;
    }
    
    // If organizationId doesn't match, this is an error
    const error = new Error(`Staff found in organization ${orgIdFromResponse}, but searched for organization ${organizationId}`);
    error.response = { status: 403, data: { message: `This user does not belong to the selected hospital organization` } };
    throw error;
  } catch (error) {
    console.error(`[staffService] Error searching staff by mobile ${mobile} in organization ${organizationId}:`, error);
    throw error;
  }
};

export const getStaffByUserId = async (userId, mobile = null) => {
  console.log(`[staffService] getStaffByUserId called for userId: ${userId}${mobile ? `, mobile: ${mobile}` : ''}`);
  
  // Get organizationId from localStorage (set by login flow with selectedOrgId)
  // During login, we set organizationId to the selected organization before calling this function
  const storedUser = localStorage.getItem("user");
  const storedOrgId = localStorage.getItem("organizationId");
  let organizationId = null;
  
  // Prefer organizationId from localStorage (set by login with selectedOrgId)
  if (storedOrgId) {
    try {
      organizationId = Number(storedOrgId);
      if (!isNaN(organizationId) && organizationId > 0) {
        console.log(`[staffService] Using organizationId ${organizationId} from localStorage (selected during login)`);
      } else {
        organizationId = null;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  // If no organizationId in localStorage, try to get from user object
  if (!organizationId && storedUser) {
    try {
      const user = JSON.parse(storedUser);
      const userOrgId = user?.organizationId;
      if (userOrgId != null && userOrgId !== "" && !isNaN(userOrgId) && Number(userOrgId) > 0) {
        organizationId = Number(userOrgId);
        console.log(`[staffService] Using organizationId ${organizationId} from user object`);
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  // If we have organizationId AND mobile, use mobile search (more reliable)
  // The login API may return wrong userId, but mobile number is more reliable
  if (organizationId && mobile) {
    try {
      console.log(`[staffService] Using mobile search (more reliable) for mobile ${mobile} in organization ${organizationId}`);
      return await getStaffByMobile(mobile, organizationId);
    } catch (error) {
      // If mobile search fails, fall through to userId search
      console.warn(`[staffService] Mobile search failed, will try userId search:`, error.message);
    }
  }
  
  // If we have organizationId (but no mobile or mobile search failed), try to fetch staff from that organization first
  // If it fails (user doesn't exist in that org), try without organizationId to search across all orgs
  if (organizationId) {
    try {
      console.log(`[staffService] Fetching staff for userId ${userId} in organization ${organizationId}`);
      const params = { organizationId: organizationId };
      // If mobile is provided, include it as a parameter (backend will try mobile first, then userId)
      if (mobile) {
        params.mobile = mobile.trim();
      }
      
      const response = await apiClient.get(`${API_URL}/user/${userId}`, { params });
      const data = response.data;
      
      // Verify the response has matching organizationId
      const orgIdFromResponse = data?.organizationId || data?.organization_id;
      if (orgIdFromResponse && Number(orgIdFromResponse) === organizationId) {
        console.log(`[staffService] ✅ Staff found for userId ${userId} in organization ${organizationId}`);
        return data;
      }
      
      // If organizationId doesn't match, this is an error
      console.warn(`[staffService] ⚠️ OrganizationId mismatch: expected ${organizationId}, got ${orgIdFromResponse}`);
      // Fall through to search without organizationId
    } catch (error) {
      console.warn(`[staffService] Staff not found for userId ${userId} in organization ${organizationId}, will search across all organizations`);
      // Fall through to search without organizationId
      organizationId = null; // Clear organizationId to search across all orgs
    }
  }
  
  // If organizationId was not provided or fetch failed, search without organizationId
  // This allows the backend to search across all organizations
  
  // Try without organizationId (backend should search across all organization tables)
  // Save the selected organizationId before clearing it
  const tempOrgId = localStorage.getItem("organizationId");
  
  // Use axios directly (bypassing apiClient interceptor) to search without organizationId
  // This ensures the backend can search across all organization tables
  try {
    console.log(`[staffService] Calling /staff/user/${userId} without organizationId (searching across all organizations)...`);
    // Use axios directly with the base URL to bypass apiClient interceptor
    const token = localStorage.getItem('token');
    const response = await axios.get(`${BACKEND_URL}${API_URL}/user/${userId}`, {
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });
    const data = response.data;
    
    console.log(`[staffService] ✅ Staff found for userId ${userId}`);
    console.log(`[staffService] Full response data:`, data);
    
    // Check for organizationId in response
    const orgIdFromResponse = data?.organizationId || data?.organization_id;
    if (orgIdFromResponse != null && orgIdFromResponse !== "" && !isNaN(orgIdFromResponse) && Number(orgIdFromResponse) > 0) {
      const foundOrgId = Number(orgIdFromResponse);
      console.log(`[staffService] ✅ Staff found with organizationId: ${foundOrgId}`);
      
      // If we had a selected organizationId, validate that it matches
      // Use tempOrgId (originally selected) instead of localStorage (which we temporarily cleared)
      if (tempOrgId && Number(tempOrgId) !== foundOrgId) {
        console.warn(`[staffService] ⚠️ Found staff in organization ${foundOrgId}, but selected organization is ${tempOrgId}`);
        // Restore organizationId before throwing error
        if (tempOrgId) {
          localStorage.setItem("organizationId", tempOrgId);
        }
        // This means the user exists but in a different organization - this is an error
        const error = new Error(`Staff found in organization ${foundOrgId}, but login was attempted for organization ${tempOrgId}`);
        error.response = { status: 403, data: { message: `This user does not belong to the selected hospital organization` } };
        throw error;
      }
      
      // Update localStorage with organizationId from response
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const updatedUser = { ...user, organizationId: foundOrgId };
          localStorage.setItem("user", JSON.stringify(updatedUser));
          localStorage.setItem("organizationId", foundOrgId.toString());
          console.log(`[staffService] ✅ Updated localStorage with organizationId: ${foundOrgId}`);
        } catch (e) {
          console.error("Error updating localStorage:", e);
        }
      }
    }
    
    return data;
  } catch (error) {
    // If backend returns 500/404 OR network error (CORS), try with common organizationIds
    // Network errors don't have error.response, so we check for that too
    const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');
    const isServerError = error.response?.status === 500 || error.response?.status === 404;
    
    if (isServerError || isNetworkError) {
      console.log(`[staffService] Backend requires organizationId or network error occurred.`);
      console.log(`[staffService] Error type: ${isNetworkError ? 'Network/CORS' : 'Server Error'}, Status: ${error.response?.status}`);
      
      // First, try to get list of organizations from backend
      let organizationIdsToTry = [];
      try {
        console.log(`[staffService] Attempting to get list of organizations from backend...`);
        const organizations = await getAllHospitalOrganizations();
        if (Array.isArray(organizations) && organizations.length > 0) {
          organizationIdsToTry = organizations
            .map(org => org.id || org.organizationId || org.organization_id)
            .filter(id => id != null && !isNaN(id) && Number(id) > 0)
            .map(id => Number(id));
          console.log(`[staffService] ✅ Got ${organizationIdsToTry.length} organizations from backend:`, organizationIdsToTry);
        }
      } catch (orgListError) {
        console.warn(`[staffService] Could not get organization list from backend, will use fallback range 1-${MAX_ORGANIZATION_ID}`);
      }
      
      // If we couldn't get the list, use fallback range
      if (organizationIdsToTry.length === 0) {
        console.log(`[staffService] Using fallback range: organizationIds 1-${MAX_ORGANIZATION_ID}`);
        console.log(`[staffService] Note: If your organization ID is > ${MAX_ORGANIZATION_ID}, set REACT_APP_MAX_ORG_ID environment variable`);
        for (let i = 1; i <= MAX_ORGANIZATION_ID; i++) {
          organizationIdsToTry.push(i);
        }
      }
      
      // Try each organizationId
      for (const orgId of organizationIdsToTry) {
        try {
          console.log(`[staffService] Trying with organizationId: ${orgId}...`);
          const response = await apiClient.get(`${API_URL}/user/${userId}`, {
            params: { organizationId: orgId }
          });
          
          if (response.data) {
            const data = response.data;
            console.log(`[staffService] ✅✅✅ FOUND staff for userId ${userId} in organization ${orgId}!!!`);
            
            // Update localStorage
            if (storedUser) {
              try {
                const user = JSON.parse(storedUser);
                const updatedUser = { ...user, organizationId: orgId };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                localStorage.setItem("organizationId", orgId.toString());
                console.log(`[staffService] ✅✅✅ UPDATED localStorage with organizationId: ${orgId}`);
                console.log(`[staffService] Updated user object:`, updatedUser);
              } catch (e) {
                console.error("Error updating localStorage:", e);
              }
            }
            
            return data;
          }
        } catch (orgError) {
          // Continue to next organizationId (silently for 404/500/network errors)
          const isOrgNetworkError = !orgError.response && (orgError.code === 'ERR_NETWORK' || orgError.message === 'Network Error');
          if (orgError.response?.status !== 404 && orgError.response?.status !== 500 && !isOrgNetworkError) {
            console.warn(`[staffService] Error checking organization ${orgId}:`, orgError.message);
          }
        }
      }
      
      // If not found, try fallback endpoint with organizationIds
      console.log(`[staffService] Trying fallback endpoint /staff/${userId} with organizationIds...`);
      for (const orgId of organizationIdsToTry) {
        try {
          const response = await apiClient.get(`${API_URL}/${userId}`, {
            params: { organizationId: orgId }
          });
          const data = response.data;
          const orgIdFromResponse = data?.organizationId || data?.organization_id;
          if (orgIdFromResponse || data) {
            console.log(`[staffService] ✅✅✅ FOUND staff via fallback endpoint in organization ${orgId}!!!`);
            if (storedUser) {
              try {
                const user = JSON.parse(storedUser);
                const updatedUser = { ...user, organizationId: Number(orgIdFromResponse || orgId) };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                localStorage.setItem("organizationId", (orgIdFromResponse || orgId).toString());
                console.log(`[staffService] ✅✅✅ UPDATED localStorage with organizationId: ${orgIdFromResponse || orgId}`);
              } catch (e) {
                console.error("Error updating localStorage:", e);
              }
            }
            return data;
          }
        } catch (fallbackError) {
          // Continue to next organizationId
        }
      }
    }
    
    // If all attempts failed, throw the original error
    console.error(`[staffService] ❌ Staff not found for userId ${userId} after trying all methods`);
    console.error(`[staffService] Original error:`, error.message || error);
    throw error;
  }
};

export const updateStaffByUserId = async (id, staff) => {
  try {
    // Clean up staff data: ensure empty strings are converted to null for optional fields
    const cleanedStaff = {
      ...staff,
      departmentId: staff.departmentId && staff.departmentId !== "" ? staff.departmentId : null,
      departmentName: staff.departmentName && staff.departmentName !== "" ? staff.departmentName : null,
      specialty: staff.specialty && staff.specialty !== "" ? staff.specialty : null,
      registrationNumber: staff.registrationNumber && staff.registrationNumber !== "" ? staff.registrationNumber : null,
    };
    
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(`${API_URL}/user/${id}`, cleanedStaff);
    
    if (response.status === 200) {
      // Get organizationId from response or localStorage for user update
      let organizationId = null;
      
      // First, try to get from response data (source of truth)
      const orgIdFromResponse = response.data?.organizationId || response.data?.organization_id;
      if (orgIdFromResponse != null && orgIdFromResponse !== "" && !isNaN(orgIdFromResponse)) {
        organizationId = Number(orgIdFromResponse);
      }
      
      // If not in response, try localStorage
      if (!organizationId) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            const orgId = user?.organizationId;
            if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
              organizationId = Number(orgId);
            }
          } catch (e) {
            console.error("Error parsing stored user:", e);
          }
        }
      }
      
      // Update user in user management service
      const data = {
        id: id,                 
        full_name: response.data.name,
        user_pwd: response.data.password,
        user_role: response.data.userRole,
        email: response.data.email,
        department: response.data.departmentId ? response.data.departmentId : "",
        mobile: response.data.mobile,
        // Include organization_id if available (similar to admin user updates)
        ...(organizationId && { organization_id: organizationId }),
      };
      
      try {
        await userManagementApi.post(
          "/updateUser",
          data
        );
      } catch (userError) {
        console.warn(
          "Error updating user in user management service:",
          userError
        );

        // Don't fail staff update if User Management update fails
      }
    }

    invalidateStaffCache();
    return response.data;
  } catch (error) {
    console.error("Error updating staff:", error);
    // Re-throw with more context
    if (error.response) {
      const errorMessage = error.response.data?.message || error.response.data?.details || "Failed to update staff";
      throw new Error(errorMessage);
    }
    throw error;
  }
};

export const deleteUserRole = async (id) => {
  const response = await apiClient.delete(`${API_URL}/${id}`);
  return response.data;
};
